import { Component, HostBinding, Input } from '@angular/core';

/**
 * Order "icon + content" meta row — the repeated
 *   <div class="order-detail-row">
 *     <span class="order-detail-row__icon"><i class="fa …"></i></span>
 *     <span class="order-detail-row__content">…</span>
 *   </div>
 * pattern from the order screens (store-order-details meta list and the
 * store-orders mobile card list), centralised so the grid geometry lives in
 * one place instead of being duplicated in both feature SCSS files.
 *
 * The icon is projected via the `meta-icon` slot (supports conditional /
 * multi-`<i>` icons), the row body via default content. The host element IS
 * the `.order-detail-row` grid, so the rendered DOM matches the original.
 *
 *   <app-order-meta-row rowClass="col-md-12 px-1 px-md-2 mb-3"
 *                       contentTestId="order-customer-name">
 *     <i meta-icon class="fa fa-user"></i>
 *     {{ order.customerName }}
 *   </app-order-meta-row>
 */
@Component({
  selector: 'app-order-meta-row',
  templateUrl: './order-meta-row.component.html',
  styleUrls: ['./order-meta-row.component.scss'],
})
export class OrderMetaRowComponent {
  /** Extra classes for the host row (column/spacing utilities). */
  @Input() rowClass = '';
  /** Optional data-testid applied to the content cell (e2e parity). */
  @Input() contentTestId: string | null = null;
  /** Vertical alignment of icon vs content. `start` (default) suits
   *  multi-line content; `center` matches the single-line mobile cards.
   *  Deliberately NOT named `align`: a static `align="center"` attribute is a
   *  native presentational hint the browser maps to `text-align: center`,
   *  which the row content inherits. */
  @Input() verticalAlign: 'start' | 'center' = 'start';

  @HostBinding('class')
  get hostClasses(): string {
    return ('order-detail-row ' + this.rowClass).trim();
  }

  @HostBinding('class.order-detail-row--center')
  get isCenter(): boolean {
    return this.verticalAlign === 'center';
  }
}
